import {
  createSelector,
  createSlice,
  PayloadAction,
  Selector
} from '@reduxjs/toolkit';
import { HarnessFlagSet } from 'adapters/types/Client';
import { REDUX_SLICE_KEYS, ROLES } from 'common/constants';
import { kebabToCamelCase } from './helpers';
import type { UserDataType } from './types';
import type { RootState } from 'store';

const initialState: UserDataType = {
  userRole: ROLES.UNAUTHORIZED,
  harnessFlags: {},
  isLoginFailed: false
};

export const userDataSlice = createSlice({
  name: REDUX_SLICE_KEYS.userData,
  initialState,
  reducers: {
    setUserRole: (state, action: PayloadAction<string>) => {
      state.userRole = action?.payload;
    },
    setHarnessFlags: (state, action: PayloadAction<HarnessFlagSet>) => {
      state.harnessFlags = action?.payload;
    },
    setIsLoginFailed: (state, action: PayloadAction<boolean>) => {
      state.isLoginFailed = action?.payload;
    },
    setJWTFlag: (state, action: PayloadAction<boolean>) => {
      state.harnessFlags = {
        ...state.harnessFlags,
        dms_mod_accounting_jwt_token_enable: action.payload
      };
    }
  }
});

export const { setUserRole, setHarnessFlags, setIsLoginFailed, setJWTFlag } =
  userDataSlice.actions;

const selectSelf = (state: RootState) => state;

export const getUserRole = createSelector(
  selectSelf,
  ({ userData }) => userData.userRole
);

export const getUserRoleIsApOfficeManager = createSelector(
  getUserRole,
  getRole => getRole === ROLES.AP_OFFICE_MANAGER
);

export const getHarnessFlags = createSelector(
  selectSelf,
  ({ userData }) => userData.harnessFlags
);

export const getIsLoginFailed = createSelector(
  selectSelf,
  ({ userData }) => userData.isLoginFailed
);

export const getFormattedHarnessFlags = createSelector(
  getHarnessFlags,
  (flags): Record<string, boolean> =>
    Object.entries(flags).reduce((acc, [flagKey, flagValue]) => {
      const key = kebabToCamelCase(flagKey);
      return {
        ...acc,
        [key]: !!flagValue
      };
    }, {})
);

export const getHarnessFlagValue = (
  flagKey: string
): Selector<RootState, boolean | undefined> =>
  createSelector(
    getFormattedHarnessFlags,
    flags => flags[flagKey] ?? undefined
  );

export default userDataSlice.reducer;
